/**
 * Add / edit hotel stay form — mirrors iOS AddHotelStayView (manual) + EditHotelStayView.
 */

import React, { useEffect, useState } from 'react';
import { Modal, Form, Input, DatePicker, Button, List, Space, Popconfirm, message } from 'antd';
import dayjs, { type Dayjs } from 'dayjs';
import type { TripHotelConfirmation, TripHotelStay } from '../../types/client';
import { tripLodgingEn } from '../../content/tripLodging.en';
import { newHotelStayId, normalizeConfirmations } from '../../utils/hotelStayHelpers';

export interface HotelStayFormValues {
  hotelName: string;
  address?: string;
  phone?: string;
  checkInDate: string;
  checkOutDate: string;
  notes?: string;
  confirmations: TripHotelConfirmation[];
}

const HotelStayFormModal: React.FC<{
  open: boolean;
  mode: 'add' | 'edit';
  initial?: TripHotelStay | null;
  defaultCheckIn: string;
  defaultCheckOut: string;
  canDelete?: boolean;
  onClose: () => void;
  onSave: (values: HotelStayFormValues & { id: string }) => void;
  onDelete?: () => void;
  onSearchInstead?: () => void;
}> = ({
  open,
  mode,
  initial,
  defaultCheckIn,
  defaultCheckOut,
  canDelete,
  onClose,
  onSave,
  onDelete,
  onSearchInstead,
}) => {
  const [form] = Form.useForm();
  const [confirmations, setConfirmations] = useState<TripHotelConfirmation[]>([]);
  const [confDraft, setConfDraft] = useState('');
  const [editingConfId, setEditingConfId] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    const confs = normalizeConfirmations(initial?.confirmations);
    setConfirmations(confs);
    setConfDraft('');
    setEditingConfId(null);
    form.setFieldsValue({
      hotelName: initial?.hotelName ?? '',
      address: initial?.address ?? '',
      phone: initial?.phone ?? '',
      checkInDate: dayjs(initial?.checkInDate || defaultCheckIn),
      checkOutDate: dayjs(initial?.checkOutDate || defaultCheckOut),
      notes: initial?.notes ?? '',
      confirmationNumber: mode === 'add' ? '' : undefined,
    });
  }, [open, initial, defaultCheckIn, defaultCheckOut, form, mode]);

  const handleAddOrUpdateConfirmation = (): void => {
    const number = confDraft.trim();
    if (!number) return;
    if (editingConfId) {
      setConfirmations((prev) =>
        prev.map((c) =>
          c.id === editingConfId ? { ...c, confirmationNumber: number } : c
        )
      );
      setEditingConfId(null);
    } else {
      setConfirmations((prev) => [
        ...prev,
        { id: crypto.randomUUID(), confirmationNumber: number },
      ]);
    }
    setConfDraft('');
  };

  const handleFinish = (values: {
    hotelName: string;
    address?: string;
    phone?: string;
    checkInDate: Dayjs;
    checkOutDate: Dayjs;
    notes?: string;
    confirmationNumber?: string;
  }): void => {
    const checkInDate = values.checkInDate.format('YYYY-MM-DD');
    const checkOutDate = values.checkOutDate.format('YYYY-MM-DD');
    if (checkOutDate <= checkInDate) {
      message.error(tripLodgingEn.checkOutAfterCheckIn);
      return;
    }
    let nextConfs = confirmations;
    if (mode === 'add' && values.confirmationNumber?.trim()) {
      nextConfs = [
        ...confirmations,
        {
          id: crypto.randomUUID(),
          confirmationNumber: values.confirmationNumber.trim(),
        },
      ];
    }
    onSave({
      id: initial?.id ?? newHotelStayId(),
      hotelName: values.hotelName.trim(),
      address: values.address?.trim() || undefined,
      phone: values.phone?.trim() || undefined,
      checkInDate,
      checkOutDate,
      notes: values.notes?.trim() || undefined,
      confirmations: nextConfs,
    });
  };

  return (
    <Modal
      open={open}
      title={mode === 'edit' ? tripLodgingEn.editHotel : tripLodgingEn.addHotel}
      onCancel={onClose}
      footer={null}
      destroyOnClose
      width={520}
    >
      <Form form={form} layout="vertical" onFinish={handleFinish}>
        <Form.Item
          name="hotelName"
          label={tripLodgingEn.hotelName}
          rules={[{ required: true, message: tripLodgingEn.hotelNameRequired }]}
        >
          <Input />
        </Form.Item>
        <Form.Item name="address" label={tripLodgingEn.address}>
          <Input />
        </Form.Item>
        <Form.Item name="phone" label={tripLodgingEn.phone}>
          <Input />
        </Form.Item>
        <Form.Item name="checkInDate" label={tripLodgingEn.checkIn} rules={[{ required: true }]}>
          <DatePicker style={{ width: '100%' }} />
        </Form.Item>
        <Form.Item name="checkOutDate" label={tripLodgingEn.checkOut} rules={[{ required: true }]}>
          <DatePicker style={{ width: '100%' }} />
        </Form.Item>

        {mode === 'add' ? (
          <Form.Item name="confirmationNumber" label={tripLodgingEn.confirmationNumber}>
            <Input />
          </Form.Item>
        ) : (
          <div style={{ marginBottom: 16 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
              <strong>{tripLodgingEn.confirmations}</strong>
            </div>
            {confirmations.length === 0 ? (
              <p style={{ color: 'var(--text-color-light)', fontStyle: 'italic' }}>
                {tripLodgingEn.noConfirmations}
              </p>
            ) : (
              <List
                size="small"
                dataSource={confirmations}
                renderItem={(item) => (
                  <List.Item
                    actions={[
                      <Button
                        key="edit"
                        type="link"
                        size="small"
                        onClick={() => {
                          setEditingConfId(item.id);
                          setConfDraft(item.confirmationNumber);
                        }}
                      >
                        {tripLodgingEn.editConfirmation}
                      </Button>,
                      <Button
                        key="del"
                        type="link"
                        size="small"
                        danger
                        onClick={() =>
                          setConfirmations((prev) => prev.filter((c) => c.id !== item.id))
                        }
                      >
                        Delete
                      </Button>,
                    ]}
                  >
                    {item.confirmationNumber}
                  </List.Item>
                )}
              />
            )}
            <Space.Compact style={{ width: '100%', marginTop: 8 }}>
              <Input
                value={confDraft}
                onChange={(e) => setConfDraft(e.target.value)}
                placeholder={tripLodgingEn.confirmationNumber}
              />
              <Button type="primary" onClick={handleAddOrUpdateConfirmation}>
                {editingConfId ? tripLodgingEn.save : tripLodgingEn.addConfirmation}
              </Button>
            </Space.Compact>
          </div>
        )}

        <Form.Item name="notes" label={tripLodgingEn.notes}>
          <Input.TextArea rows={3} placeholder={tripLodgingEn.notesPlaceholder} />
        </Form.Item>

        <Space wrap>
          <Button type="primary" htmlType="submit">
            {tripLodgingEn.save}
          </Button>
          <Button onClick={onClose}>{tripLodgingEn.cancel}</Button>
          {onSearchInstead ? (
            <Button type="link" onClick={onSearchInstead}>
              {tripLodgingEn.searchInstead}
            </Button>
          ) : null}
          {mode === 'edit' && canDelete && onDelete ? (
            <Popconfirm
              title={tripLodgingEn.deleteConfirm}
              onConfirm={onDelete}
              okText={tripLodgingEn.deleteStay}
              okType="danger"
            >
              <Button danger>{tripLodgingEn.deleteStay}</Button>
            </Popconfirm>
          ) : null}
        </Space>
      </Form>
    </Modal>
  );
};

export default HotelStayFormModal;
